import React, { useMemo, useState, useEffect } from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, 
  ResponsiveContainer, Cell, ReferenceLine, LineChart, 
  Line, AreaChart, Area, ComposedChart, PieChart, Pie
} from 'recharts';
import { 
  Users, FileQuestion, Activity, AlertCircle, TrendingDown, 
  CheckCircle, ArrowUpRight, Clock, Zap, Target, Gauge, 
  TrendingUp, AlertTriangle, MousePointer2, ClipboardX,
  FastForward, TimerOff, Info, Shield, Globe, Landmark,
  Monitor, Cpu, CreditCard, Sparkles, History, RefreshCw,
  MapPin, ShieldCheck, UserCircle, Radar, Signal,
  Wand2, Layout
} from 'lucide-react';
import { StorageService } from '../../services/storageService';
import { CNA_DOMAINS, US_STATES } from '../../data/mockData';
import { User } from '../../types';

export const AdminDashboard: React.FC = () => {
  const [now, setNow] = useState(new Date());
  const [users, setUsers] = useState<User[]>([]);
  const [globalStats, setGlobalStats] = useState<any>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 10000);
    return () => clearInterval(timer);
  }, []);

  const loadData = async () => {
    setIsRefreshing(true);
    try {
      const [userData, statsData] = await Promise.all([
        StorageService.getGlobalUsers({ limit: 50 }),
        StorageService.getDashboardStats()
      ]);
      setUsers(userData.items || []);
      setGlobalStats(statsData);
    } catch (e) {
      console.error("[Dashboard] Registry sync failed:", e);
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    loadData();
    const syncInterval = setInterval(loadData, 30000);
    return () => clearInterval(syncInterval);
  }, []);

  const analytics = useMemo(() => {
    // Defensive check to ensure users is always an array before filtering
    const allUsers = Array.isArray(users) ? users : [];
    const students = allUsers.filter(u => u.role !== 'admin');
    
    const activityList: any[] = [];
    let concurrentLive = 0;
    let practitionersCount = 0; // Users currently in an active session
    const LIVE_THRESHOLD_MS = 300000; // 5 Minutes window for "Live / Online"

    students.forEach(s => {
        // Read history from user document or stats array
        const userHistory = (Array.isArray(s.history) && s.history.length > 0)
            ? s.history
            : (Array.isArray((s as any).stats?.history) && (s as any).stats.history.length > 0)
                ? (s as any).stats.history
                : [];
        
        const lastActiveTime = s.lastActive 
            ? new Date(s.lastActive).getTime() 
            : (s.updatedAt ? new Date(s.updatedAt).getTime() : 0);
        const timeSinceActive = lastActiveTime > 0 ? (now.getTime() - lastActiveTime) : Infinity;
        const isLive = timeSinceActive < LIVE_THRESHOLD_MS;
        
        if (isLive) {
            concurrentLive++;
            if (s.currentSession) practitionersCount++;
        }

        const latestHistoryItem = userHistory.length > 0 ? userHistory[0] : null;
        let lastScore: number | null = null;
        if (latestHistoryItem && typeof latestHistoryItem.score === 'number') {
            lastScore = Math.round(latestHistoryItem.score);
        } else if (s.stats?.averageScore && typeof s.stats.averageScore === 'number' && s.stats.averageScore > 0) {
            lastScore = Math.round(s.stats.averageScore);
        }

        let lastAction = 'Awaiting Entry';
        if (s.currentSession) {
            const sessionObj = typeof s.currentSession === 'object' ? s.currentSession : {};
            const mode = sessionObj.mode || sessionObj.type || '';
            if (mode === 'exam' || mode === 'simulator') {
                lastAction = 'Taking Exam';
            } else if (mode === 'quiz' || mode === 'practice') {
                lastAction = 'Practicing Quiz';
            } else if (mode === 'flashcards') {
                lastAction = 'Flashcards Study';
            } else {
                lastAction = 'Active Session';
            }
        } else if (isLive) {
            lastAction = userHistory.length > 0 ? 'Navigating Hub' : 'Hub Active';
        } else {
            lastAction = userHistory.length > 0 ? 'Completed Session' : 'Offline';
        }

        activityList.push({
            name: s.name || 'Anonymous Candidate',
            email: s.email || 'N/A',
            state: s.state && s.state !== 'Global' ? s.state : 'Global',
            lastActive: s.lastActive,
            timeSinceActive,
            action: lastAction,
            score: lastScore,
            isLive,
            tier: s.planTier || 'free'
        });
    });

    return {
        totalStudents: globalStats?.totalStudents || students.length,
        proRate: globalStats?.proRate || 0,
        avgPassRate: globalStats?.avgPassRate || 0,
        globalAvgScore: globalStats?.globalAvgScore || 0,
        totalItemsProcessed: globalStats?.totalItemsProcessed || 0,
        recentActivity: activityList.sort((a, b) => {
            if (a.isLive !== b.isLive) return a.isLive ? -1 : 1;
            return a.timeSinceActive - b.timeSinceActive;
        }).slice(0, 15),
        liveUsers: concurrentLive,
        practitioners: practitionersCount
    };
  }, [users, globalStats, now]);

  const stateDistribution = useMemo(() => {
    return globalStats?.stateDistribution || [];
  }, [globalStats]);

  return (
    <div className="space-y-6 md:space-y-8 animate-fade-in pb-12 px-2 md:px-0">
      <div className="flex flex-col lg:flex-row justify-between items-stretch gap-6">
        <div className="flex-1 bg-slate-900 rounded-[2rem] md:rounded-[3rem] p-6 md:p-10 text-white shadow-2xl relative overflow-hidden group border border-white/5">
             <div className="absolute top-0 right-0 w-96 h-96 bg-medical-500/10 blur-[120px] rounded-full -translate-y-1/2 translate-x-1/2"></div>
             <div className="relative z-10">
                <div className="flex items-center gap-3 mb-6">
                    <div className="px-3 py-1 bg-emerald-500/20 text-emerald-400 text-[8px] md:text-[10px] font-black rounded-full border border-emerald-500/30 flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></div>
                        Heartbeat: 60s
                    </div>
                    <span className="text-slate-500 text-[9px] md:text-[10px] font-black tracking-widest">{now.toLocaleTimeString()}</span>
                </div>
                <h2 className="text-3xl md:text-5xl font-black tracking-tighter leading-none mb-4">Registry<br/>Intelligence</h2>
                <p className="text-slate-400 font-medium text-xs md:text-sm max-w-md opacity-80">Real-time telemetry aggregated across jurisdictional partitions.</p>
             </div>
        </div>

        <div className="w-full lg:w-[450px] bg-white rounded-[2rem] md:rounded-[3.3rem] border border-slate-200 p-6 md:p-10 shadow-xl flex flex-col justify-between relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-6 text-slate-50 opacity-10 group-hover:scale-110 transition-transform">
                <Signal size={180}/>
            </div>
            <div className="relative z-10 flex flex-col h-full">
                <div className="flex justify-between items-start mb-6 md:mb-8">
                    <div>
                        <h3 className="text-[10px] md:text-xs font-black text-slate-400 tracking-widest">Active Pulse</h3>
                        <p className="text-xs md:text-sm font-black text-slate-800 tracking-tight mt-1">Live Environment</p>
                    </div>
                    <div className={`flex items-center gap-2 px-2.5 py-1 md:px-3 md:py-1.5 rounded-xl border-2 transition-all ${analytics.liveUsers > 0 ? 'bg-emerald-50 border-emerald-100 text-emerald-600 animate-pop' : 'bg-slate-50 border-slate-100 text-slate-400'}`}>
                        <div className={`w-1.5 h-1.5 md:w-2 md:h-2 rounded-full ${analytics.liveUsers > 0 ? 'bg-emerald-500 animate-ping' : 'bg-slate-300'}`}></div>
                        <span className="text-[8px] md:text-[10px] font-black tracking-widest">{analytics.liveUsers > 0 ? 'LIVE' : 'IDLE'}</span>
                    </div>
                </div>

                <div className="flex-1 grid grid-cols-2 gap-4 md:gap-8 items-center">
                    <div className="text-center md:text-left border-r border-slate-100">
                        <p className="text-5xl md:text-7xl font-black text-slate-900 leading-none tracking-tighter">{analytics.liveUsers}</p>
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 tracking-widest mt-2 md:mt-3">Connected</p>
                    </div>
                    <div className="space-y-3 md:space-y-4">
                        <div className="flex items-center gap-3 md:gap-4">
                            <div className="w-8 h-8 md:w-10 md:h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center shadow-sm"><Wand2 size={18}/></div>
                            <div>
                                <p className="text-base md:text-lg font-black text-slate-800 leading-none">{analytics.practitioners}</p>
                                <p className="text-[7px] md:text-[8px] font-black text-slate-400 tracking-widest">In Session</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 md:gap-4">
                            <div className="w-8 h-8 md:w-10 md:h-10 rounded-xl bg-medical-50 text-medical-600 flex items-center justify-center shadow-sm"><Layout size={18}/></div>
                            <div>
                                <p className="text-base md:text-lg font-black text-slate-800 leading-none">{Math.max(0, analytics.liveUsers - analytics.practitioners)}</p>
                                <p className="text-[7px] md:text-[8px] font-black text-slate-400 tracking-widest">Navigation</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mt-6 md:mt-8 pt-4 md:pt-6 border-t border-slate-50 flex items-center justify-between">
                    <div className="flex gap-1">
                        {Array.from({length: Math.min(8, analytics.liveUsers)}).map((_, i) => (
                            <div key={i} className="w-1 h-4 md:h-6 rounded-full bg-emerald-500/30 animate-pulse" style={{ animationDelay: `${i * 150}ms` }}></div>
                        ))}
                    </div>
                    <button onClick={loadData} className={`p-2 bg-slate-50 rounded-xl transition-all border border-slate-100 ${isRefreshing ? 'animate-spin text-medical-600' : 'text-slate-300 hover:text-medical-600 hover:bg-white active:scale-90'}`}>
                        <RefreshCw size={16} />
                    </button>
                </div>
            </div>
        </div>
      </div>

      {/* KPI Matrix */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
        <AdminStatCard icon={Users} label="Candidates" value={analytics.totalStudents.toString()} color="bg-sky-600" trend="Registry Depth" />
        <AdminStatCard icon={Target} label="Success" value={`${analytics.avgPassRate}%`} color="bg-emerald-600" trend="Accuracy" />
        <AdminStatCard icon={CreditCard} label="Conversion" value={`${analytics.proRate}%`} color="bg-indigo-600" trend="Pro Tiers" />
        <AdminStatCard icon={Activity} label="Logic Run" value={analytics.totalItemsProcessed.toLocaleString()} color="bg-rose-600" trend="Exposure" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
        <div className="lg:col-span-8 space-y-6 md:space-y-8">
            <div className="bg-white rounded-[2rem] md:rounded-[2.5rem] border border-slate-200 shadow-sm overflow-hidden flex flex-col min-h-[500px] md:min-h-[600px]">
                <div className="p-6 md:p-8 border-b border-slate-50 flex items-center justify-between bg-slate-50/30">
                    <div>
                        <h3 className="text-lg md:text-xl font-black text-slate-900 tracking-tight flex items-center gap-3">
                            <Globe size={20} className="text-indigo-600" /> Operational Heartbeat
                        </h3>
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 tracking-widest mt-1">Real-time status tracking via logic bridge</p>
                    </div>
                </div>
                
                <div className="overflow-x-auto flex-1 custom-scrollbar">
                    <table className="w-full text-left border-collapse min-w-[600px]">
                        <thead className="bg-slate-50/50 sticky top-0 z-10">
                            <tr>
                                <th className="py-4 px-6 md:px-8 text-[8px] md:text-[9px] font-black text-slate-400 tracking-widest">Candidate Identity</th>
                                <th className="py-4 px-4 md:px-6 text-[8px] md:text-[9px] font-black text-slate-400 tracking-widest text-center">Jurisdiction</th>
                                <th className="py-4 px-4 md:px-6 text-[8px] md:text-[9px] font-black text-slate-400 tracking-widest text-center">Connectivity</th>
                                <th className="py-4 px-4 md:px-6 text-[8px] md:text-[9px] font-black text-slate-400 tracking-widest">Active State</th>
                                <th className="py-4 px-6 md:px-8 text-right text-[8px] md:text-[9px] font-black text-slate-400 tracking-widest">Last Exam</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {analytics.recentActivity.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="py-12 text-center text-slate-400 font-medium text-xs">
                                        No active candidate telemetry recorded.
                                    </td>
                                </tr>
                            ) : (
                                analytics.recentActivity.map((log, idx) => (
                                    <tr key={idx} className={`group hover:bg-slate-50 transition-colors ${log.isLive ? 'bg-medical-50/10' : ''}`}>
                                        <td className="py-4 md:py-5 px-6 md:px-8">
                                            <div className="flex items-center gap-3 md:gap-4">
                                                <div className={`w-8 h-8 md:w-10 md:h-10 rounded-xl flex items-center justify-center font-black text-[10px] md:text-xs shadow-md shrink-0 ${log.isLive ? 'bg-emerald-600 text-white ring-4 ring-emerald-500/20' : 'bg-slate-900 text-white'}`}>
                                                    {log.name.charAt(0).toUpperCase()}
                                                </div>
                                                 <div className="min-w-0">
                                                    <p className="font-black text-slate-800 text-[10px] md:text-[11px] truncate max-w-[120px] md:max-w-[140px]">{log.name}</p>
                                                    <p className="text-[8px] md:text-[9px] text-slate-400 font-bold truncate max-w-[120px] md:max-w-[140px]">{log.email}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="py-4 md:py-5 px-4 md:px-6 text-center">
                                            <div className="flex items-center justify-center gap-1.5 md:gap-2">
                                                <MapPin size={12} className="text-medical-500 shrink-0" />
                                                <span className="text-[9px] font-black text-slate-600 whitespace-nowrap">{log.state}</span>
                                            </div>
                                        </td>
                                        <td className="py-4 md:py-5 px-4 md:px-6 text-center">
                                            <div className={`inline-flex items-center gap-1.5 md:gap-2 px-2 py-0.5 md:px-2.5 md:py-1 rounded-lg border text-[7px] md:text-[8px] font-black whitespace-nowrap ${log.isLive ? 'bg-emerald-50 border-emerald-100 text-emerald-600' : 'bg-slate-50 border-slate-100 text-slate-400'}`}>
                                                <div className={`w-1 md:w-1.5 h-1 md:h-1.5 rounded-full ${log.isLive ? 'bg-emerald-500 animate-pulse' : 'bg-slate-300'}`}></div>
                                                {log.isLive ? 'Online' : 'Offline'}
                                            </div>
                                        </td>
                                        <td className="py-4 md:py-5 px-4 md:px-6">
                                            <span className={`text-[9px] md:text-[10px] font-black whitespace-nowrap truncate block max-w-[120px] ${log.isLive ? 'text-indigo-600 font-bold' : 'text-slate-400'}`}>{log.action}</span>
                                        </td>
                                        <td className="py-4 md:py-5 px-6 md:px-8 text-right">
                                            <span className={`text-xs md:text-sm font-black ${log.score !== null && log.score >= 75 ? 'text-emerald-600' : log.score !== null && log.score > 0 ? 'text-rose-600' : 'text-slate-300'}`}>
                                                {log.score !== null && log.score !== undefined ? `${log.score}%` : '--'}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <div className="lg:col-span-4 space-y-6 md:space-y-8">
            <div className="bg-white rounded-[2rem] md:rounded-[2.5rem] p-6 md:p-8 border border-slate-200 shadow-sm">
                <h3 className="text-base md:text-lg font-black text-slate-900 mb-6 md:mb-8 flex items-center gap-2 tracking-tight">
                    <ShieldCheck size={20} className="text-indigo-600" /> Load Distribution
                </h3>
                <div className="space-y-5 md:space-y-6">
                    {stateDistribution.map(item => (
                        <div key={item.state} className="space-y-1.5 md:space-y-2">
                            <div className="flex justify-between items-end">
                                <div>
                                    <p className="text-[9px] md:text-[10px] font-black text-slate-800 tracking-tighter truncate max-w-[180px]">{item.state} Board</p>
                                    <span className="text-[7px] md:text-[8px] font-bold text-emerald-600">Registry Allocation</span>
                                </div>
                                <span className="text-[10px] font-xs font-black text-slate-400 tabular-nums">{item.count}</span>
                            </div>
                            <div className="h-1 md:h-1.5 w-full bg-slate-50 rounded-full overflow-hidden border border-slate-100">
                                <div 
                                    className="h-full bg-indigo-500 rounded-full transition-all duration-1000 shadow-[0_0_8px_rgba(99,102,241,0.2)]" 
                                    style={{ width: `${(item.count / Math.max(analytics.totalStudents, 1)) * 100}%` }}
                                ></div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="bg-slate-900 rounded-[2rem] md:rounded-[2.5rem] p-8 md:p-10 text-white relative overflow-hidden shadow-2xl border border-white/5">
                <div className="absolute top-0 right-0 p-6 opacity-10">
                    <Cpu size={120} />
                </div>
                <h3 className="text-[8px] md:text-[10px] font-black text-medical-400 tracking-[0.3em] mb-6 md:mb-8">System Reliability</h3>
                <div className="space-y-5 md:space-y-6 relative z-10">
                    <div className="flex items-center gap-4 md:gap-5">
                        <div className="p-3 md:p-4 bg-white/5 rounded-xl md:rounded-2xl border border-white/10 text-medical-400 shadow-xl"><Shield size={24}/></div>
                        <div>
                            <p className="text-2xl md:text-3xl font-black tabular-nums">99.99%</p>
                            <p className="text-[8px] md:text-[9px] font-black text-slate-500 tracking-widest mt-1">Uptime SLA</p>
                        </div>
                    </div>
                    <div className="p-4 bg-white/5 rounded-2xl border border-white/10 space-y-4 text-center">
                         <p className="text-[8px] md:text-[9px] font-bold text-slate-400 leading-relaxed">"Session monitoring and logout hooks ensure that offline statuses propagate within 120 seconds of inactivity or exit."</p>
                    </div>
                </div>
            </div>
        </div>
      </div>
    </div>
  );
};

const AdminStatCard: React.FC<{ icon: any, label: string, value: string, color: string, trend: string }> = ({ icon: Icon, label, value, color, trend }) => (
    <div className="bg-white p-4 md:p-8 rounded-[1.5rem] md:rounded-[2.5rem] shadow-sm border border-slate-100 flex flex-col md:flex-row items-center md:items-center justify-between gap-3 group hover:border-medical-500 transition-all duration-500 active:scale-95">
        <div className="text-center md:text-left order-2 md:order-1">
            <p className="text-[8px] md:text-[10px] font-black text-slate-400 tracking-widest mb-1 md:mb-2">{label}</p>
            <p className="text-xl md:text-3xl font-black text-slate-900 group-hover:scale-105 transition-transform origin-left tabular-nums">{value}</p>
            <p className="hidden md:flex text-[9px] font-bold text-slate-400 mt-2 items-center gap-1.5">
                <ArrowUpRight size={10} className="text-emerald-500" /> {trend}
            </p>
        </div>
        <div className={`w-10 h-10 md:w-16 md:h-16 rounded-xl md:rounded-2xl ${color} flex items-center justify-center text-white shadow-xl md:shadow-2xl group-hover:rotate-6 transition-all order-1 md:order-2 shrink-0`}>
            <Icon size={32} />
        </div>
    </div>
);